import {
  compact,
  some,
  sumBy,
  uniq,
} from 'lodash';
import {
  MParticlePageName,
  PageViewEvent,
} from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { Paths } from './url_helpers';
import CoreClient from '../../lib/core_client';
import { IUser } from '@reverbdotcom/commons';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import experiments from '@reverbdotcom/commons/src/experiments';

export interface CartItem {
  payment_methods: Record<string, any>;
  product_id: string | number;
  item_price: { amount: string };
  quantity?: number;
}

export async function fetchCartItems(): Promise<CartItem[]> {
  const headers = {
    'X-Display-Currency': 'USD',
  };

  const response = await CoreClient.get<{ cart_items: CartItem[] }>(Paths.apiCart.expand({}), { headers });
  return response.cart_items;
}

interface CheckoutPageViewParams {
  cartItems: CartItem[];
  pageName: MParticlePageName;
  paymentMethod?: string;
  user: Partial<IUser>;
}

// Tested by __test__/routes/checkout.test.ts
export function buildCheckoutPageView({ cartItems, pageName, paymentMethod, user }: CheckoutPageViewParams): PageViewEvent {
  const filteredCartItems = cartItemsForPaymentMethod(cartItems, paymentMethod);
  const itemTotalPrice = getTotal(filteredCartItems, 'item_price');
  const shippingTotalPrice = getTotal(filteredCartItems, 'shipping_price');
  const negotiatedOrder = some(filteredCartItems, { type: 'offer' });
  const sellerCount = getSellerCount(cartItems);

  return {
    pageName,
    experiments: formatExperimentsForEventAttributes(user, [
      experiments.CART_CHECKOUT_BUTTON,
    ]),
    paymentMethod: paymentMethod && paymentMethod.toUpperCase(),
    itemCount: filteredCartItems.length,
    sellerCount,
    totalPrice: itemTotalPrice.toFixed(2),
    totalShippingRate: shippingTotalPrice.toFixed(2),
    'negotiatedOrder?': negotiatedOrder,
  };
}

function cartItemsForPaymentMethod(cartItems: CartItem[], paymentMethod: string) {
  if (!paymentMethod) return cartItems;
  return cartItems.filter(item => item.payment_methods.hasOwnProperty(paymentMethod));
}

function getTotal(cartItems, key) {
  return sumBy(
    cartItems,
    (item) => {
      return (item[key] && parseFloat(item[key].amount)) || 0;
    },
  );
}

function getSellerCount(cartItems) {
  const sellerLinks = uniq(compact(cartItems.map(cartItem => cartItem.seller_id)));
  return sellerLinks.length;
}
